import type { Execution } from '@deskworks/shared-types';
import { CheckCheck, Zap } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useToast } from '@/lib/toast';

export interface Mark {
  executionId: string;
  read: boolean;
}

/**
 * A person's messages the agent at work has not answered yet, and whether it has read them
 * (docs/features/work-item-chat.md#writing-to-the-agent-while-it-works). Only where the run reports what its agent saw
 * (a CLI runtime: `canInterrupt`). `reply`: a chat, the messages since the agent's last reply; `run`: a run on the
 * work, the messages since it started (not those to the Lead).
 */
export function readMarks(
  messages: { id: string; seq: number; senderType: string; createdAt: string; metadata?: Record<string, unknown> }[],
  execution: Execution | null | undefined,
  since: 'reply' | 'run',
): Map<string, Mark> {
  const marks = new Map<string, Mark>();
  if (!execution?.canInterrupt || execution.status !== 'running') return marks;
  const started = new Date(execution.startedAt ?? execution.createdAt).getTime();
  const lastReply = messages.findLast((m) => m.senderType === 'agent')?.seq ?? 0;
  for (const m of messages) {
    if (m.senderType !== 'user') continue;
    if (since === 'reply' ? m.seq <= lastReply : new Date(m.createdAt).getTime() < started || m.metadata?.toLead === true) continue;
    marks.set(m.id, { executionId: execution.id, read: m.seq <= (execution.deliveredSeq ?? 0) });
  }
  return marks;
}

/** Under such a message: "Read", or that it reaches the agent with its next step, with "Take it in now". */
export function ReadMark({ mark, agentName }: { mark: Mark; agentName: string }) {
  const toast = useToast();
  const [stopping, setStopping] = useState(false);
  if (mark.read) {
    return (
      <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <CheckCheck className="size-3" /> {t('Read by {agent}', { agent: agentName })}
      </span>
    );
  }
  const interrupt = async () => {
    setStopping(true);
    try {
      await api.executions.interrupt(mark.executionId);
    } catch (err) {
      setStopping(false);
      toast.error(t('Could not stop the step'), errorMessage(err));
    }
  };
  return (
    <span className="mt-0.5 inline-flex flex-wrap items-center justify-end gap-x-2 text-[11px] text-muted-foreground">
      {stopping ? t('{agent} stops its step and reads it …', { agent: agentName }) : t('{agent} reads it with its next step', { agent: agentName })}
      {!stopping && (
        <button
          type="button"
          onClick={() => void interrupt()}
          className="inline-flex cursor-pointer items-center gap-0.5 font-medium text-indigo-700 hover:underline dark:text-indigo-300"
          title={t('Stops what the agent is doing right now (like Esc in Claude Code); it goes on with your message.')}
        >
          <Zap className="size-3" /> {t('Take it in now')}
        </button>
      )}
    </span>
  );
}
